<template>
  <a-modal
    :open="visible"
    :destroy-on-close="true"
    title="Рендер GLTF"
    ok-text="Запуск"
    cancel-text="Отмена"
    :confirm-loading="isSubmitting"
    width="80%"
    :body-style="{ maxHeight: '80vh', overflowY: 'auto' }"
    centered
    @ok="onStart"
    @cancel="closeModal">
    <a-form layout="vertical">
      <gltf-render-config-editor
        ref="gltfConfigEditor"
        v-model="gltfConfigText"
        :plan="plan"
        @update:has-errors="hasGltfConfigErrors = $event" />

      <a-form-item
        label="Изображение (опционально)"
        style="margin-top: 0.5rem;">
        <a-upload
          :before-upload="handleBeforeUpload"
          :show-upload-list="false"
          accept=".jpg,.jpeg,.png,.webp,.svg">
          <a-button type="default">
            Загрузить изображение
          </a-button>
        </a-upload>
        <div
          v-if="selectedImageName"
          style="margin-top: 0.5rem;">
          {{ selectedImageName }}
          <a-button
            size="small"
            type="link"
            @click="clearImage">
            Удалить
          </a-button>
        </div>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { api } from '@/api';
import { launchWithConfirm } from '@/helpers/launchWithConfirm';
import GltfRenderConfigEditor from '@/Components/Common/GltfRenderConfigEditor.vue';

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({}),
  },
  visible: {
    type: Boolean,
    default: () => false,
  },
});

const emit = defineEmits(['close', 'success']);

const gltfConfigEditor = ref(null);
const gltfConfigText = ref('');
const hasGltfConfigErrors = ref(false);
const isSubmitting = ref(false);
const selectedImage = ref(null);
const selectedImageName = ref('');

const normalizeConfigToString = (config) => {
  if (config === null || config === undefined) {
    return '';
  }
  if (typeof config === 'string') {
    return config;
  }
  try {
    return JSON.stringify(config, null, 2);
  } catch (e) {
    return '';
  }
};

const resetData = () => {
  hasGltfConfigErrors.value = false;
  selectedImage.value = null;
  selectedImageName.value = '';
  gltfConfigText.value = normalizeConfigToString(props.plan?.plan?.gltf_config);
};

watch(
  () => props.visible,
  (newValue) => {
    if (!newValue) return;

    resetData();
  },
);

const closeModal = () => {
  resetData();
  emit('close');
};

const handleBeforeUpload = (file) => {
  selectedImage.value = file;
  selectedImageName.value = file.name;
  return false;
};

const clearImage = () => {
  selectedImage.value = null;
  selectedImageName.value = '';
};

const onStart = async () => {
  const validationMessage = gltfConfigEditor.value?.getValidationMessage();
  if (hasGltfConfigErrors.value || validationMessage) {
    message.error(validationMessage || 'Исправьте ошибки в JSON конфиге');
    return;
  }

  const formData = new FormData();
  const configValue = gltfConfigText.value?.trim();
  if (configValue) {
    formData.append('gltf_config', configValue);
  }
  if (selectedImage.value) {
    formData.append('image', selectedImage.value);
  }

  isSubmitting.value = true;
  try {
    const response = await launchWithConfirm(
      (force, choice) => api.runGltfRender(props.plan.id, formData, force, choice),
    );
    if (!response) return;

    message.success('GLTF рендер запущен');
    closeModal();
    emit('success');
  } catch (error) {
    const responseErrors = error?.response?.data?.errors;
    const responseMessage = error?.response?.data?.message;
    const firstError = responseErrors ? Object.values(responseErrors).flat()[0] : null;
    message.error(firstError || responseMessage || 'Ошибка запуска GLTF рендера');
  } finally {
    isSubmitting.value = false;
  }
};
</script>
